import { useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { sideBias, timeframeBias, type PivotPoint } from '../lib/structureBias3';
import { timelineWindow } from '../lib/timelineZoom';
import { StructureRoomCard } from './StructureRoom';

export type PublicFrame = { timeframe: string; state: string; asOfUtc: string; highs: PivotPoint[]; lows: PivotPoint[];
  latestHigh?: { label: string; price: number; atUtc: string } | null; latestLow?: { label: string; price: number; atUtc: string } | null };
const colors: Record<string, string> = { HH: '#38b96a', LH: '#d76baf', HL: '#4e9fe0', LL: '#c65e55' };
const eastern = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const easternDate = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' });

function etDateParts(time: number) {
  const values = Object.fromEntries(easternDate.formatToParts(new Date(time)).map(p => [p.type, p.value]));
  return { year: Number(values.year), month: Number(values.month), day: Number(values.day) };
}
function cashTime(year: number, month: number, day: number, hour: number, minute: number) {
  const rough = Date.UTC(year, month - 1, day, 14, 0);
  const zone = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', timeZoneName: 'shortOffset' })
    .formatToParts(new Date(rough)).find(p => p.type === 'timeZoneName')?.value ?? 'GMT-5';
  const offset = Number(zone.replace('GMT', '')) || 0;
  return Date.UTC(year, month - 1, day, hour - offset, minute);
}
function sessionMarks(start: number, end: number) {
  const result: Array<{ at: number; label: string }> = [];
  const seen = new Set<string>();
  for (let t = start - 86400_000; t <= end + 86400_000; t += 86400_000) {
    const { year, month, day } = etDateParts(t);
    const key = `${year}-${month}-${day}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
    if (weekday === 0 || weekday === 6) continue;
    for (const [hour, minute, label] of [[9, 30, 'OPEN'], [16, 0, 'CLOSE']] as const) {
      const at = cashTime(year, month, day, hour, minute);
      if (at >= start && at <= end) result.push({ at, label });
    }
  }
  return result;
}
function ColorTrack({ name, points, start, end, bullishLabel, bearishLabel }: {
  name: string; points: PivotPoint[]; start: number; end: number; bullishLabel: string; bearishLabel: string;
}) {
  const bias = sideBias(points, bullishLabel, bearishLabel);
  const ordered = [...points].filter(p => Number.isFinite(Date.parse(p.atUtc))).sort((a,b) => Date.parse(a.atUtc) - Date.parse(b.atUtc));
  const marks = sessionMarks(start, end);
  return <div className="trend-track"><div className="trend-track-title"><span>{name}</span><strong className={bias}>{bias}</strong></div><div className="trend-track-bar" aria-label={`${name}: ${ordered.map(p => p.label).join(', ') || 'no confirmed pivots'}`}>
    {ordered.slice(1).map((point, index) => {
      const previous = ordered[index];
      const segmentStart = Math.max(start, Date.parse(previous.atUtc));
      const segmentEnd = Math.min(end, Date.parse(point.atUtc));
      if (segmentStart >= end || segmentEnd <= start || segmentEnd <= segmentStart) return null;
      return <span key={`${previous.atUtc}-${point.atUtc}`} className="trend-color-segment"
        title={`${point.label} segment · ${eastern.format(new Date(previous.atUtc))}–${eastern.format(new Date(point.atUtc))} ET`}
        style={{ left: `${(segmentStart - start) / (end - start) * 100}%`, width: `${(segmentEnd - segmentStart) / (end - start) * 100}%`, background: colors[point.label] ?? '#26343f' }} />;
    })}
    {marks.map(mark => <span key={`${mark.at}-${mark.label}`} className="trend-session-mark" title={`${mark.label} · ${eastern.format(new Date(mark.at))} ET`}
      style={{ left: `${(mark.at - start) / (end - start) * 100}%` }} />)}
  </div></div>;
}

export function TrendTimeline({ frame, observedAtUtc, price, stale }: { frame: PublicFrame; observedAtUtc: string; price: number | null; stale: boolean }) {
  const [zoom, setZoom] = useState(0);
  const end = Date.parse(observedAtUtc);
  const earliest = Math.min(...[...frame.highs, ...frame.lows].map(p => Date.parse(p.atUtc)).filter(Number.isFinite), end);
  const fullStart = frame.timeframe === '4h' ? Math.max(end - 20 * 86400_000, Math.min(earliest, end - 7 * 86400_000))
    : frame.timeframe === '1h' ? Math.max(end - 5 * 86400_000, Math.min(earliest, end - 86400_000)) : end - 24 * 60 * 60_000;
  const window = timelineWindow(end, fullStart, frame.timeframe, zoom);
  const start = window.start;
  const bias = timeframeBias(frame.highs, frame.lows);
  const marks = sessionMarks(start, end);
  return <article className="structure-strip trend-timeline"><div className="structure-strip-heading"><strong>{frame.timeframe.toUpperCase()}</strong><div className="trend-heading-actions"><span className={`bias-pill ${bias}`}>{bias}</span><div className="trend-zoom" aria-label={`${frame.timeframe} timeline zoom`}>
    <button type="button" aria-label={`Zoom out ${frame.timeframe}`} title="Show more time" disabled={!window.canZoomOut} onClick={() => setZoom(value => value - 1)}><Minus size={14}/></button>
    <span>{window.label}</span>
    <button type="button" aria-label={`Zoom in ${frame.timeframe}`} title="Focus on recent time" disabled={!window.canZoomIn} onClick={() => setZoom(value => value + 1)}><Plus size={14}/></button>
  </div></div></div>
    <ColorTrack name="HIGHS" points={frame.highs} start={start} end={end} bullishLabel="HH" bearishLabel="LH"/>
    <ColorTrack name="LOWS" points={frame.lows} start={start} end={end} bullishLabel="HL" bearishLabel="LL"/>
    <div className="trend-time-axis"><span>{eastern.format(new Date(start))} ET</span><span>{eastern.format(new Date(end))} ET</span></div>
    <div className="trend-session-legend">{marks.length ? `${marks.map(m => `${m.label} ${eastern.format(new Date(m.at))}`).join(' · ')} ET` : 'No cash open or close inside this span'}</div>
    <StructureRoomCard frame={frame} price={price} stale={stale}/>
    <p>Two consecutive same-color segments confirm the highs or lows. Both sides must agree; otherwise the timeframe stays sideways. Completed pivot-to-pivot segments use the ending pivot's color; unfinished segments stay neutral.</p>
  </article>;
}
